import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef
} from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { applyFontSize } from './custom-size-style';

@Component({
	selector: 'app-font-size-input',
	templateUrl: './font-size-input.component.html',
	styleUrls: ['./font-size-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class FontSizeInputComponent implements OnInit, OnDestroy {
	@Input() quillEditor: any;
	@Output() sizeChanged = new EventEmitter<string>();
	
	@ViewChild('fontSizeInput', { static: true }) fontSizeInput!: ElementRef<HTMLInputElement>;
	
	currentSize: string = '';
	displayValue: string = '';
	isMultiple: boolean = false;
	private destroy$ = new Subject<void>();
	private isUpdating: boolean = false;
	private savedSelection: any = null;
	
	constructor(private cdr: ChangeDetectorRef) {}
	
	ngOnInit() {
		if (this.quillEditor) {
			this.initializeQuillListeners();
			this.initializeInputListeners();
			this.updateDisplay();
		}
	}
	
	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
	
	private initializeQuillListeners() {
		// Wait for the quillEditor to be fully initialized
		setTimeout(() => {
			const quill = this.quillEditor?.quillEditor;
			
			if (quill && typeof quill.on === 'function') {
				// Listen to selection changes
				quill.on('selection-change', (range: any) => {
					if (!this.isUpdating) {
						// Small delay to ensure the DOM is updated
						setTimeout(() => {
							this.updateDisplay();
						}, 10);
					}
				});
				
				// Listen to text changes
				quill.on('text-change', () => {
					if (!this.isUpdating) {
						setTimeout(() => {
							this.updateDisplay();
						}, 10);
					}
				});
				
				// Also listen to editor clicks to update display
				const editorElement = quill.root;
				if (editorElement) {
					editorElement.addEventListener('click', () => {
						if (!this.isUpdating) {
							setTimeout(() => {
								this.updateDisplay();
							}, 50);
						}
					});
				}
			} else {
				console.warn('Quill editor not fully initialized for font-size component');
			}
		}, 500);
	}
	
	private initializeInputListeners() {
		// Debounce input changes
		fromEvent(this.fontSizeInput.nativeElement, 'input')
			.pipe(
				takeUntil(this.destroy$),
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(() => {
				this.validateInput();
			});
	}
	
	updateDisplay() {
		const quill = this.quillEditor?.quillEditor;
		if (!quill) return;
		
		const range = quill.getSelection();
		if (!range) {
			this.displayValue = '';
			this.isMultiple = false;
			this.cdr.markForCheck();
			return;
		}
		
		const formats = quill.getFormat(range);
		const size = this.getSelectionSize(formats);
		
		if (size === 'multiple') {
			this.displayValue = 'Multiple...';
			this.isMultiple = true;
		} else if (size) {
			this.displayValue = this.extractNumericSize(size);
			this.isMultiple = false;
		} else {
			this.displayValue = '';
			this.isMultiple = false;
		}
		
		this.currentSize = this.displayValue;
		this.cdr.markForCheck();
	}
	
	private getSelectionSize(formats: any): string | null {
		const quill = this.quillEditor?.quillEditor;
		if (!quill || !formats) return null;
		
		// Check for size format (could be our custom pixel value)
		if (formats.size !== undefined) {
			if (Array.isArray(formats.size)) {
				return 'multiple';
			} else if (typeof formats.size === 'string') {
				return formats.size;
			} else if (formats.size === false) {
				// false means default size
				return null;
			}
		}
		
		// If no size format, try to get computed size from the DOM
		const range = quill.getSelection();
		if (range && range.length > 0) {
			try {
				// Get the actual DOM element for the selection
				const [leaf] = quill.getLeaf(range.index);
				if (leaf && leaf.domNode && leaf.domNode.parentElement) {
					const computedStyle = window.getComputedStyle(leaf.domNode.parentElement);
					const fontSize = computedStyle.fontSize;
					if (fontSize && fontSize !== 'inherit') {
						return fontSize;
					}
				}
			} catch (e) {
				// Fallback if we can't get the computed style
				console.debug('Could not get computed font size', e);
			}
		}
		
		// If no size format and can't get computed, return null (will use default)
		return null;
	}
	
	private extractNumericSize(size: string): string {
		if (!size) return '';
		
		// Handle Quill's predefined sizes
		const sizeMap: { [key: string]: string } = {
			'small': '10',
			'false': '16',
			'normal': '16',
			'large': '18',
			'huge': '32'
		};
		
		if (sizeMap[size]) {
			return sizeMap[size];
		}
		
		// Extract number from pixel value (e.g., "24px" -> "24")
		const match = size.match(/(\d+(?:\.\d+)?)/);
		if (match) {
			// Round to nearest integer if it's a decimal
			const value = Math.round(parseFloat(match[1]));
			return value.toString();
		}
		
		return '';
	}
	
	onMouseDown(event: MouseEvent) {
		// Save selection before the input gets focus
		const quill = this.quillEditor?.quillEditor;
		if (quill) {
			this.savedSelection = quill.getSelection();
		}
		// Don't prevent default - let the input get focus normally
	}
	
	onFocus() {
		// Save the current selection if not already saved
		const quill = this.quillEditor?.quillEditor;
		if (quill && !this.savedSelection) {
			this.savedSelection = quill.getSelection();
		}
		// Select all text when focused
		this.fontSizeInput.nativeElement.select();
	}
	
	onBlur() {
		// Apply the font size when focus is lost
		this.applyFontSize();
	}
	
	onKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.applyFontSize();
			// Return focus to editor
			const quill = this.quillEditor?.quillEditor;
			if (quill) {
				quill.focus();
			}
		} else if (event.key === 'Escape') {
			event.preventDefault();
			this.displayValue = this.currentSize;
			this.cdr.markForCheck();
			const quill = this.quillEditor?.quillEditor;
			if (quill) {
				quill.focus();
			}
		}
	}
	
	onInput(event: Event) {
		const input = event.target as HTMLInputElement;
		const value = input.value;
		
		// Allow only numbers
		if (value !== 'Multiple...' && !/^\d*$/.test(value)) {
			input.value = value.replace(/[^\d]/g, '');
			this.displayValue = input.value;
		} else {
			this.displayValue = value;
		}
		
		this.cdr.markForCheck();
	}
	
	private validateInput() {
		const value = this.displayValue.trim();
		
		if (value && value !== 'Multiple...' && value !== '') {
			const numericValue = parseInt(value, 10);
			
			// Clamp value between 8 and 200
			if (numericValue < 8) {
				this.displayValue = '8';
			} else if (numericValue > 200) {
				this.displayValue = '200';
			}
			
			this.cdr.markForCheck();
		}
	}
	
	applyFontSize() {
		const quill = this.quillEditor?.quillEditor;
		if (!quill) return;
		
		const value = this.displayValue.trim();
		
		// Don't apply if it's showing "Multiple..." or empty
		if (!value || value === 'Multiple...') {
			this.updateDisplay();
			return;
		}
		
		const numericValue = parseInt(value, 10);
		
		// Validate size range
		if (isNaN(numericValue) || numericValue < 8 || numericValue > 200) {
			this.updateDisplay();
			return;
		}
		
		// Use saved selection or get current selection
		const range = this.savedSelection || quill.getSelection();
		if (range) {
			this.isUpdating = true;
			
			// Restore selection if it was saved
			if (this.savedSelection) {
				quill.setSelection(this.savedSelection);
			}
			
			// Apply font size using the custom function that handles whitelisting
			const sizeValue = `${numericValue}px`;
			applyFontSize(quill, range, sizeValue);
			
			// Clear saved selection after use
			this.savedSelection = null;
			
			// Emit the change
			this.sizeChanged.emit(`${numericValue}px`);
			
			setTimeout(() => {
				this.isUpdating = false;
				this.updateDisplay();
			}, 100);
		}
	}
	
	private mapToQuillSize(px: number): string | false {
		// Map pixel sizes to Quill's predefined sizes or return custom size
		if (px <= 10) return 'small';
		if (px <= 13) return false; // false = normal/default
		if (px <= 18) return 'large';
		if (px >= 32) return 'huge';
		
		// For in-between sizes, use the pixel value
		return `${px}px`;
	}
}